import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Inbox, Mail, CalendarClock, Users, MessageSquare, Download, StickyNote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "new", label: "New", cls: "bg-gray-100 text-gray-600", active: "bg-gray-700 text-white" },
  { value: "replied", label: "Replied", cls: "bg-[#7F9F43]/10 text-[#6e8b39]", active: "bg-[#7F9F43] text-white" },
  { value: "scheduled", label: "Scheduled", cls: "bg-[#E89A4D]/10 text-[#c97f35]", active: "bg-[#E89A4D] text-white" },
];

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:border-[#7F9F43] focus:ring-2 focus:ring-[#7F9F43]/15 transition-[border-color,box-shadow] duration-300";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white border border-gray-100 card-soft p-8 space-y-5" data-testid="admin-login-form">
        <img src="/assets/huminence-wordmark-green.png" alt="Huminence" className="h-6 w-auto" />
        <div>
          <h1 className="text-xl font-semibold text-[#111827]">Inquiry Dashboard</h1>
          <p className="text-sm text-gray-400 mt-1">Sign in to view demo requests.</p>
        </div>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
        <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
        <button type="submit" disabled={loading} className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#7F9F43] px-6 py-3 text-sm font-semibold text-white hover:bg-[#6e8b39] transition-colors duration-300 disabled:opacity-60" data-testid="admin-login-submit">
          {loading && <Loader2 size={15} className="animate-spin" />}
          Sign In
        </button>
      </form>
    </div>
  );
};

const NoteEditor = ({ contact, onSave }) => {
  const [draft, setDraft] = useState(contact.notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = draft !== (contact.notes || "");

  const save = async () => {
    setSaving(true);
    await onSave(contact.id, draft);
    setSaving(false);
  };

  return (
    <div className="mt-3" data-testid={`inquiry-notes-${contact.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <StickyNote size={13} className="text-[#E89A4D]" />
        <span className="text-[11px] tracking-[0.15em] uppercase text-gray-400 font-semibold">Private notes</span>
      </div>
      <div className="flex gap-2 items-start">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a follow-up note..."
          rows={draft ? 2 : 1}
          data-testid={`inquiry-notes-input-${contact.id}`}
          className="flex-1 rounded-xl border border-dashed border-[#E89A4D]/40 bg-[#E89A4D]/[0.04] px-4 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-[#E89A4D] resize-none transition-[border-color] duration-300"
        />
        {dirty && (
          <button
            onClick={save}
            disabled={saving}
            data-testid={`inquiry-notes-save-${contact.id}`}
            className="rounded-full bg-[#E89A4D] px-4 py-2 text-xs font-semibold text-white hover:bg-[#d9873a] transition-colors duration-300 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("inquiries");

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/contacts`, { headers: authHeader() }),
        axios.get(`${API}/newsletter`, { headers: authHeader() }),
      ]);
      setContacts(c.data);
      setSubscribers(s.data);
    } catch {
      toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem("admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (user) fetchContacts(); }, [user, fetchContacts]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: authHeader() });
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/contacts/${id}/status`, { status }, { headers: authHeader() });
      setContacts((c) => c.map((x) => (x.id === id ? { ...x, status } : x)));
      if (data.confirmation_email_sent) toast.success("Marked as scheduled — confirmation email sent.");
      else toast.success("Status updated.");
    } catch {
      toast.error("Status update failed.");
    }
  };

  const removeSubscriber = async (id) => {
    try {
      await axios.delete(`${API}/newsletter/${id}`, { headers: authHeader() });
      setSubscribers((s) => s.filter((x) => x.id !== id));
      toast.success("Subscriber removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/contacts/${id}/notes`, { notes }, { headers: authHeader() });
      setContacts((c) => c.map((x) => (x.id === id ? { ...x, notes } : x)));
      toast.success("Note saved.");
    } catch {
      toast.error("Could not save note.");
    }
  };

  const exportCsv = () => {
    const rows = [["Email", "Subscribed At"], ...subscribers.map((s) => [s.email, new Date(s.created_at).toISOString()])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `huminence-subscribers-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Subscriber list exported.");
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setUser(null);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#7F9F43]" /></div>;
  if (!user) return <Login onLogin={setUser} />;

  const visible = filter === "all" ? contacts : contacts.filter((c) => (c.status || "new") === filter);
  const countOf = (v) => (v === "all" ? contacts.length : contacts.filter((c) => (c.status || "new") === v).length);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/huminence-wordmark-green.png" alt="Huminence" className="h-5 w-auto" />
            <span className="text-xs tracking-[0.2em] uppercase text-gray-400 font-semibold hidden sm:block">Inquiry Dashboard</span>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#111827] transition-colors duration-300" data-testid="admin-logout">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex rounded-full bg-white border border-gray-200 p-1 gap-1 mb-3 w-fit" data-testid="admin-view-tabs">
              <button
                onClick={() => setView("inquiries")}
                data-testid="admin-view-inquiries"
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 ${
                  view === "inquiries" ? "bg-[#7F9F43] text-white" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <MessageSquare size={13} /> Inquiries ({contacts.length})
              </button>
              <button
                onClick={() => setView("subscribers")}
                data-testid="admin-view-subscribers"
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 ${
                  view === "subscribers" ? "bg-[#7F9F43] text-white" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <Users size={13} /> Subscribers ({subscribers.length})
              </button>
            </div>
            <h1 className="text-2xl font-semibold text-[#111827]">
              {view === "inquiries" ? "Inquiries" : "Newsletter Subscribers"}
            </h1>
          </div>
          {view === "subscribers" && subscribers.length > 0 && (
            <button
              onClick={exportCsv}
              data-testid="export-subscribers-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#111827] px-5 py-2.5 text-xs font-semibold text-white hover:bg-[#2b3444] transition-colors duration-300"
            >
              <Download size={14} /> Export CSV
            </button>
          )}
          {view === "inquiries" && (
          <div className="flex rounded-full bg-white border border-gray-200 p-1 gap-1" data-testid="admin-filter-tabs">
            {[{ value: "all", label: "All" }, ...STATUSES].map((s) => (
              <button
                key={s.value}
                onClick={() => setFilter(s.value)}
                data-testid={`admin-filter-${s.value}`}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 ${
                  filter === s.value ? "bg-[#111827] text-white" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {s.label} <span className="opacity-60">({countOf(s.value)})</span>
              </button>
            ))}
          </div>
          )}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#7F9F43]" /></div>
        ) : view === "subscribers" ? (
          subscribers.length === 0 ? (
            <div className="rounded-3xl bg-white border border-gray-100 card-soft p-16 text-center">
              <Users className="mx-auto text-gray-300" size={36} />
              <p className="mt-4 text-gray-400 text-sm">No subscribers yet.</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-gray-100 card-soft divide-y divide-gray-100" data-testid="subscribers-list">
              {subscribers.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-6 py-4" data-testid={`subscriber-row-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-[#7F9F43]/10 flex items-center justify-center text-[#7F9F43]">
                      <Mail size={15} />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-[#111827]">{s.email}</p>
                      <p className="text-xs text-gray-400">{new Date(s.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <button onClick={() => removeSubscriber(s.id)} aria-label="Remove subscriber" data-testid={`subscriber-delete-${s.id}`} className="h-9 w-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors duration-300">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )
        ) : visible.length === 0 ? (
          <div className="rounded-3xl bg-white border border-gray-100 card-soft p-16 text-center">
            <Inbox className="mx-auto text-gray-300" size={36} />
            <p className="mt-4 text-gray-400 text-sm">{filter === "all" ? "No inquiries yet." : `No ${filter} inquiries.`}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((c) => (
              <div key={c.id} className="rounded-2xl bg-white border border-gray-100 card-soft p-6" data-testid={`inquiry-card-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="font-semibold text-[#111827]">{c.name}</p>
                      {c.interest && (
                        <span className="text-[11px] font-semibold tracking-wide uppercase rounded-full bg-[#7F9F43]/10 text-[#6e8b39] px-3 py-1">{c.interest}</span>
                      )}
                      {c.role && (
                        <span className="text-[11px] font-semibold tracking-wide uppercase rounded-full bg-[#E89A4D]/10 text-[#c97f35] px-3 py-1">{c.role}</span>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-[#7F9F43] transition-colors duration-300">
                        <Mail size={13} /> {c.email}
                      </a>
                      {c.organization && <span>{c.organization}</span>}
                      {(c.preferred_date || c.preferred_time) && (
                        <span className="inline-flex items-center gap-1.5 text-[#c97f35]">
                          <CalendarClock size={13} />
                          {[c.preferred_date, c.preferred_time].filter(Boolean).join(" · ")}
                        </span>
                      )}
                      <span className="text-gray-300">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-full border border-gray-200 p-1 gap-1" data-testid={`inquiry-status-${c.id}`}>
                      {STATUSES.map((s) => (
                        <button
                          key={s.value}
                          onClick={() => setStatus(c.id, s.value)}
                          data-testid={`inquiry-status-${s.value}-${c.id}`}
                          className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors duration-300 ${
                            (c.status || "new") === s.value ? s.active : "text-gray-400 hover:text-gray-600"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => remove(c.id)} aria-label="Delete inquiry" data-testid={`inquiry-delete-${c.id}`} className="h-9 w-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors duration-300">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-600 leading-relaxed bg-[#F9FAFB] rounded-xl px-4 py-3">{c.message}</p>
                <NoteEditor contact={c} onSave={saveNotes} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
